import React, { useState, useMemo } from 'react';
import { Drawer, Upload, Button, Input, Empty, Popconfirm, Space, Typography, Tag, Tooltip, message } from 'antd';
import { UploadOutlined, DeleteOutlined, CheckOutlined, SearchOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';

import { DbSchoolImage } from '../db/types';
import * as ds from '../db/dataService';
import { useDb } from '../db/useDataService';
import { CropModal } from './AdImageEditor';

const { Text } = Typography;

interface Props {
  visible: boolean;
  onClose: () => void;
  schoolId: string;
  /** Колбек выбора картинки. Если не передан — режим просмотра/управления. */
  onSelect?: (image: DbSchoolImage) => void;
  /** Если задано — подсвечивает выбранную сейчас. */
  selectedUrl?: string;
}

const SchoolImageLibrary: React.FC<Props> = ({ visible, onClose, schoolId, onSelect, selectedUrl }) => {
  useDb();
  const [search, setSearch] = useState('');
  const [pendingRaw, setPendingRaw] = useState<string | null>(null);
  const [pendingName, setPendingName] = useState('');

  const allImages = ds.listSchoolImages(schoolId);

  const images = useMemo(() => {
    if (!search.trim()) return allImages;
    const q = search.toLowerCase();
    return allImages.filter((img) =>
      img.name.toLowerCase().includes(q) ||
      (img.tags || []).some((t) => t.toLowerCase().includes(q)),
    );
  }, [allImages, search]);

  const handleBeforeUpload: UploadProps['beforeUpload'] = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      setPendingRaw(reader.result as string);
      setPendingName(file.name.replace(/\.[^/.]+$/, ''));
    };
    reader.readAsDataURL(file);
    return false; // prevent auto-upload
  };

  const handleCropConfirm = (croppedDataUrl: string) => {
    if (!pendingRaw) return;
    ds.addSchoolImage({
      schoolId,
      url: croppedDataUrl,
      name: pendingName || 'Без названия',
      tags: [],
    });
    message.success('Картинка добавлена в библиотеку');
    setPendingRaw(null);
    setPendingName('');
  };

  const handleDelete = (id: string) => {
    ds.deleteSchoolImage(id);
    message.success('Картинка удалена');
  };

  const handlePick = (img: DbSchoolImage) => {
    if (onSelect) {
      onSelect(img);
      onClose();
    }
  };

  return (
    <>
      <Drawer
        title={
          <Space>
            <span>Библиотека картинок школы</span>
            <Tag color="blue">{allImages.length}</Tag>
          </Space>
        }
        visible={visible}
        onClose={onClose}
        width={720}
        bodyStyle={{ padding: 16 }}
      >
        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
          <Input
            placeholder="Поиск по имени или тегу"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            prefix={<SearchOutlined />}
            allowClear
            style={{ width: 280 }}
          />
          <Upload beforeUpload={handleBeforeUpload} showUploadList={false} accept="image/*">
            <Button type="primary" icon={<UploadOutlined />}>Загрузить картинку</Button>
          </Upload>
        </Space>

        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
          Картинки шарятся между всеми продуктами этой школы. Формат — 4:3 (любая загруженная обрезается).
        </Text>

        {images.length === 0 ? (
          <Empty description={search ? 'Ничего не найдено' : 'Библиотека пуста. Загрузите первую картинку.'} style={{ padding: '64px 0' }} />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
            {images.map((img) => {
              const isSelected = selectedUrl === img.url;
              return (
                <div
                  key={img.id}
                  onClick={() => handlePick(img)}
                  style={{
                    position: 'relative',
                    cursor: onSelect ? 'pointer' : 'default',
                    border: isSelected ? '2px solid #1890ff' : '1px solid #f0f0f0',
                    borderRadius: 8,
                    overflow: 'hidden',
                    background: '#fafafa',
                  }}
                >
                  <div style={{ paddingTop: '75%', position: 'relative', background: `url(${img.url}) center/cover no-repeat` }} />
                  <div style={{ padding: 8, fontSize: 12 }}>
                    <Text ellipsis style={{ display: 'block', fontWeight: 500 }} title={img.name}>{img.name}</Text>
                    {(img.tags || []).slice(0, 3).map((t) => (
                      <Tag key={t} style={{ fontSize: 10, padding: '0 4px', marginTop: 2 }}>{t}</Tag>
                    ))}
                  </div>
                  <Space style={{ position: 'absolute', top: 6, right: 6 }}>
                    {isSelected && (
                      <Tag color="blue" icon={<CheckOutlined />} style={{ margin: 0 }}>Выбрана</Tag>
                    )}
                    <Popconfirm
                      title="Удалить из библиотеки?"
                      onConfirm={(e) => { e?.stopPropagation(); handleDelete(img.id); }}
                      onCancel={(e) => e?.stopPropagation()}
                      okText="Да"
                      cancelText="Нет"
                    >
                      <Tooltip title="Удалить">
                        <Button
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Tooltip>
                    </Popconfirm>
                  </Space>
                </div>
              );
            })}
          </div>
        )}
      </Drawer>

      {pendingRaw && (
        <CropModal
          visible
          imageSrc={pendingRaw}
          onCancel={() => { setPendingRaw(null); setPendingName(''); }}
          onCropComplete={handleCropConfirm}
        />
      )}
    </>
  );
};

export default SchoolImageLibrary;
